import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import type { FilesFilters } from "../types/files-filters.types.ts"
import { classifyFileFormat } from "./classify-file-format.ts"

interface DocumentFilterContext {
  /** Only set for the "include subfolders" case — every id in the subtree.
   *  A plain exact-folder filter is applied server-side and needs no
   *  client recheck here. */
  folderIds?: Set<string> | null
}

// `document_type`, `status`, `related_record`, the exact-folder case and the
// date range are all real server-side query params now (see
// `documentFilterParams` in useFilesData.ts) — what's left here is strictly
// what the `project-attachments` endpoint has no filter for: a MIME-category
// bucket that isn't a stored column, "owner" matched by display name rather
// than id, album membership (a document can never belong to one — that
// relationship only exists on the Attachment side of the model), and
// expanding "include subfolders" into its full descendant set.
/** Whether any document at all can pass these filters. */
export const documentsCanMatchFilters = (filters: FilesFilters): boolean =>
  // Attachment's own generic-FK "attached to" concept — a document has none.
  !filters.model_type && !filters.album

export const documentMatchesFilters = (
  document: ProjectDocument,
  filters: FilesFilters,
  { folderIds }: DocumentFilterContext = {}
): boolean => {
  if (!documentsCanMatchFilters(filters)) return false

  if (filters.file_type) {
    const category = classifyFileFormat({ name: document.file_name })
    if (category !== filters.file_type) return false
  }

  if (folderIds && (!document.folder || !folderIds.has(document.folder))) return false

  if (filters.owner && document.uploaded_by !== filters.owner) return false

  return true
}
